import { useEffect, useState, useCallback } from 'react';
import {
  Radio, RefreshCcw, Plus, FolderKanban, Activity, CircleCheck, Users,
} from 'lucide-react';
import {
  The, DauThe, TheSoLieu, NutPhu, NutChinh, NhanTrangThai, AnhDaiDien,
  ChuaCoDuLieu, DangTai,
} from '../components/ui';
import { api, LoiMayChu } from '../services/api';
import type { TrangThaiExtension, MucDanhBa, Nhom, ChienDich, TaiKhoanFB } from '../services/kieu';
import { avatarActor, tenActor } from '../services/taiKhoan';

interface Props {
  ext: TrangThaiExtension | null;
  taiKhoan: TaiKhoanFB | null;
  onLamMoi: () => void;
  onTaoBai: () => void;
}

export function ManTongQuan({ ext, taiKhoan, onLamMoi, onTaoBai }: Props) {
  const [danhBa, setDanhBa] = useState<MucDanhBa[]>([]);
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [chienDich, setChienDich] = useState<ChienDich[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);

  const tai = useCallback(async () => {
    setDangTai(true);
    // Ba lời gọi độc lập: một cái hỏng không được làm chết hai cái kia.
    // Bản cũ dùng Promise.all nên chỉ cần nhóm chưa đồng bộ là cả màn trắng.
    const [db, nh, cd] = await Promise.allSettled([
      api.danhBa(), api.nhom(), api.chienDich(),
    ]);
    if (db.status === 'fulfilled') setDanhBa(db.value.accounts || []);
    if (nh.status === 'fulfilled') setNhom(nh.value.groups || []);
    if (cd.status === 'fulfilled') setChienDich(cd.value.campaigns || []);

    const hong = [db, nh, cd].find(x => x.status === 'rejected') as PromiseRejectedResult | undefined;
    setLoi(hong ? (hong.reason instanceof LoiMayChu ? hong.reason.message : 'Một phần dữ liệu chưa tải được.') : null);
    setDangTai(false);
  }, []);

  useEffect(() => { tai(); }, [tai]);

  const lamMoiTatCa = () => { tai(); onLamMoi(); };

  const dangOnline = new Set((ext?.accounts || []).map(a => String(a.uid)));
  const soTacVu = chienDich.reduce((s, c) => s + (c.pending || 0) + (c.paused || 0), 0);
  const noiDuoc = Boolean(ext?.connected && ext?.facebookLoggedIn);

  // Số nhóm theo từng tài khoản: danh bạ không mang sẵn con số này, nhưng
  // tài khoản đang hoạt động thì suy ra được từ danh sách nhóm đã đồng bộ.
  const soNhomCua = (uid: string) => (String(ext?.uid || '') === uid ? nhom.length : null);

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      {loi && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
          {loi}
        </div>
      )}

      {/* Tài khoản đang hoạt động */}
      <The className="p-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <AnhDaiDien src={avatarActor(taiKhoan)} ten={tenActor(taiKhoan)} kichThuoc="h-16 w-16" />
            <div className="min-w-0">
              <div
                className={
                  'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold ' +
                  (noiDuoc
                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400')
                }
              >
                <Radio className="h-3 w-3" />
                {noiDuoc ? 'Tài khoản đang hoạt động' : 'Đang chờ tài khoản Facebook'}
              </div>
              <h2 className="mt-2 truncate text-xl font-bold text-slate-900 dark:text-white">
                {tenActor(taiKhoan)}
              </h2>
              {taiKhoan?.isPage && !taiKhoan.name && (
                <p className="mt-1 text-xs font-semibold text-amber-600 dark:text-amber-400">
                  Chưa đồng bộ được tên Page từ Facebook. Vui lòng mở Page hoặc tải lại Extension để cập nhật.
                </p>
              )}
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                UID:{' '}
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                  {taiKhoan?.uid || '—'}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <NutPhu onClick={lamMoiTatCa}>
              <RefreshCcw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
            <NutChinh onClick={onTaoBai}>
              <Plus className="h-4 w-4" />
              Tạo bài đăng
            </NutChinh>
          </div>
        </div>
      </The>

      {/* Ba thẻ số liệu */}
      <div className="grid gap-3 sm:grid-cols-3">
        <TheSoLieu icon={<FolderKanban className="h-5 w-5" />} so={nhom.length} nhan="Nhóm đã đồng bộ" />
        <TheSoLieu icon={<Activity className="h-5 w-5" />} so={soTacVu} nhan="Tác vụ gần đây" />
        <TheSoLieu
          icon={<CircleCheck className="h-5 w-5" />}
          so={noiDuoc ? 'Sẵn sàng' : 'Chờ kết nối'}
          nhan="Trạng thái tài khoản"
        />
      </div>

      {/* Bảng tài khoản */}
      <The>
        <DauThe
          icon={<Users className="h-4 w-4" />}
          tieuDe="Tài khoản"
          dem={danhBa.length}
          phai={
            <NutPhu onClick={lamMoiTatCa}>
              <RefreshCcw className="h-3.5 w-3.5" />
              Làm mới
            </NutPhu>
          }
        />

        {dangTai ? (
          <DangTai dong={3} />
        ) : danhBa.length === 0 ? (
          <ChuaCoDuLieu
            chu="Chưa có tài khoản nào."
            goiY="Cài Extension và đăng nhập Facebook để đồng bộ."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-400 dark:border-slate-700/60">
                    <th className="px-5 py-2.5 font-semibold">Tên</th>
                    <th className="px-3 py-2.5 font-semibold">UID</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Bạn</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Nhóm</th>
                    <th className="px-5 py-2.5 text-right font-semibold">Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {danhBa.map((tk) => {
                    // Ưu tiên trạng thái thật từ /api/extension/status. Trường
                    // `online` của danh bạ có thể là null khi không hỏi được —
                    // khi đó KHÔNG coi là offline, chỉ là chưa rõ.
                    const on = dangOnline.has(String(tk.uid)) || tk.online === true;
                    const chuaRo = !dangOnline.has(String(tk.uid)) && tk.online === null;
                    const soNhom = soNhomCua(String(tk.uid));
                    return (
                      <tr
                        key={tk.uid}
                        className={
                          'border-b border-slate-50 last:border-0 dark:border-slate-700/50 ' +
                          (on ? '' : 'opacity-55')
                        }
                      >
                        <td className="px-5 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <AnhDaiDien src={tk.avatar} ten={tk.name} />
                            <div className="min-w-0">
                              <p className="truncate font-semibold text-slate-800 dark:text-slate-100">
                                {tk.name || '(chưa có tên)'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                          {tk.uid}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-700 tabular-nums dark:text-slate-200">
                          —
                        </td>
                        <td className="px-3 py-3 text-right font-semibold text-slate-700 tabular-nums dark:text-slate-200">
                          {soNhom === null ? '—' : soNhom}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {on
                            ? <NhanTrangThai chu="Đang kết nối" mau="xanh" />
                            : chuaRo
                              ? <NhanTrangThai chu="Chưa rõ" mau="vang" />
                              : <NhanTrangThai chu="Chưa kết nối" mau="xam" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400 dark:border-slate-700/60">
              Tài khoản chưa kết nối chỉ hiện số liệu đã lưu. Mở Chrome đăng nhập tài khoản đó để cập nhật.
            </p>
          </>
        )}
      </The>
    </div>
  );
}
